import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './AdminAddSpecialist.css';

function AdminAddSpecialist() {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    bio: '',
    image: '',
    email: '',
    phone: '',
    specialization: '',
    experience_years: '',
    languages: '',
    is_active: true,
    display_order: '',
    audioUrl: '',
    // Arabic translations
    name_ar: '',
    title_ar: '',
    bio_ar: '',
    specialization_ar: '',
    // Russian translations
    name_ru: '',
    title_ru: '',
    bio_ru: '',
    specialization_ru: '',
    // German translations
    name_de: '',
    title_de: '',
    bio_de: '',
    specialization_de: '',
    // French translations
    name_fr: '',
    title_fr: '',
    bio_fr: '',
    specialization_fr: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Load specialist data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadSpecialistData();
    }
  }, [id, isEditMode]);

  const loadSpecialistData = async () => {
    try {
      setIsLoadingData(true);
      const { data, error } = await supabase
        .from('specialists')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        setFormData({
          name: data.name || '',
          title: data.title || '',
          bio: data.bio || '',
          image: data.image || '',
          email: data.email || '',
          phone: data.phone || '',
          specialization: data.specialization || '',
          experience_years: data.experience_years || '',
          languages: Array.isArray(data.languages) ? data.languages.join(', ') : (data.languages || ''),
          is_active: data.is_active !== undefined ? data.is_active : true,
          display_order: data.display_order || '',
          audioUrl: data.audio_url || '',
          // Arabic translations
          name_ar: data.name_ar || '',
          title_ar: data.title_ar || '',
          bio_ar: data.bio_ar || '',
          specialization_ar: data.specialization_ar || '',
          // Russian translations
          name_ru: data.name_ru || '',
          title_ru: data.title_ru || '',
          bio_ru: data.bio_ru || '',
          specialization_ru: data.specialization_ru || '',
          // German translations
          name_de: data.name_de || '',
          title_de: data.title_de || '',
          bio_de: data.bio_de || '',
          specialization_de: data.specialization_de || '',
          // French translations
          name_fr: data.name_fr || '',
          title_fr: data.title_fr || '',
          bio_fr: data.bio_fr || '',
          specialization_fr: data.specialization_fr || ''
        });
        // Set image preview if image URL exists
        if (data.image) {
          setImagePreview(data.image);
        }
      }
    } catch (err) {
      console.error('Error loading specialist:', err);
      setErrorMessage('Failed to load specialist data. Please try again.');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    // Handle file upload
    if (type === 'file' && files && files[0]) {
      const file = files[0];
      const inputName = e.target.name;
      
      if (inputName === 'image') {
        // Validate image file type
        if (!file.type.startsWith('image/')) {
          setErrors(prev => ({
            ...prev,
            image: 'Please select a valid image file'
          }));
          return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setErrors(prev => ({
            ...prev,
            image: 'Image size must be less than 5MB'
          }));
          return;
        }
        
        setImageFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
        
        // Clear error
        if (errors.image) {
          setErrors(prev => ({
            ...prev,
            image: ''
          }));
        }
      } else if (inputName === 'audio') {
        // Validate audio file type (MP3)
        const validAudioTypes = ['audio/mpeg', 'audio/mp3', 'audio/mpeg3', 'audio/x-mpeg-3'];
        const isValidType = validAudioTypes.includes(file.type) || file.name.toLowerCase().endsWith('.mp3');
        
        if (!isValidType) {
          setErrors(prev => ({
            ...prev,
            audio: 'Please select an MP3 audio file'
          }));
          return;
        }
        
        // Validate file size (max 20MB for audio)
        if (file.size > 20 * 1024 * 1024) {
          setErrors(prev => ({
            ...prev,
            audio: 'Audio file size must be less than 20MB'
          }));
          return;
        }
        
        setAudioFile(file);
        
        // Clear errors
        if (errors.audio) {
          setErrors(prev => ({
            ...prev,
            audio: ''
          }));
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      // Clear errors when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
    
    // Clear messages
    if (successMessage) setSuccessMessage('');
    if (errorMessage) setErrorMessage('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.bio.trim()) {
      newErrors.bio = 'Bio is required';
    } else if (formData.bio.trim().length < 20) {
      newErrors.bio = 'Bio must be at least 20 characters';
    }

    // Image is only required when adding new specialist, not when editing
    if (!isEditMode && !imageFile) {
      newErrors.image = 'Please select an image file';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (validateForm()) {
      setIsLoading(true);

      try {
        let imageUrl = formData.image; // Keep existing image if editing and no new file
        let audioUrl = formData.audioUrl; // Keep existing audio if editing and no new file

        // Upload image to Supabase Storage (only if new file is selected)
        if (imageFile) {
          const fileExt = imageFile.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = fileName;

          // Upload file
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('specialist-images')
            .upload(filePath, imageFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Upload error details:', uploadError);
            throw new Error(`Image upload failed: ${uploadError.message}`);
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('specialist-images')
            .getPublicUrl(filePath);

          imageUrl = urlData.publicUrl;
        }

        // Upload audio to Supabase Storage (only if new file is selected)
        // Using the same 'service-audio' bucket as services
        if (audioFile) {
          const fileExt = audioFile.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = fileName;

          // Upload file
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('service-audio')
            .upload(filePath, audioFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Audio upload error details:', uploadError);
            throw new Error(`Audio upload failed: ${uploadError.message}`);
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('service-audio')
            .getPublicUrl(filePath);

          audioUrl = urlData.publicUrl;
        }

        // Prepare languages array
        const languagesArray = formData.languages
          ? formData.languages.split(',').map(lang => lang.trim()).filter(lang => lang)
          : [];

        // Prepare data for database
        const specialistData = {
          name: formData.name.trim(),
          title: formData.title.trim() || null,
          bio: formData.bio.trim(),
          image: imageUrl,
          email: formData.email.trim() || null,
          phone: formData.phone.trim() || null,
          specialization: formData.specialization.trim() || null,
          experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
          languages: languagesArray.length > 0 ? languagesArray : null,
          is_active: formData.is_active,
          display_order: formData.display_order ? parseInt(formData.display_order) : null,
          audio_url: audioUrl || null
        };

        // Add translation fields if provided
        if (formData.name_ar.trim()) specialistData.name_ar = formData.name_ar.trim();
        if (formData.title_ar.trim()) specialistData.title_ar = formData.title_ar.trim();
        if (formData.bio_ar.trim()) specialistData.bio_ar = formData.bio_ar.trim();
        if (formData.specialization_ar.trim()) specialistData.specialization_ar = formData.specialization_ar.trim();
        
        if (formData.name_ru.trim()) specialistData.name_ru = formData.name_ru.trim();
        if (formData.title_ru.trim()) specialistData.title_ru = formData.title_ru.trim();
        if (formData.bio_ru.trim()) specialistData.bio_ru = formData.bio_ru.trim();
        if (formData.specialization_ru.trim()) specialistData.specialization_ru = formData.specialization_ru.trim();
        
        if (formData.name_de.trim()) specialistData.name_de = formData.name_de.trim();
        if (formData.title_de.trim()) specialistData.title_de = formData.title_de.trim();
        if (formData.bio_de.trim()) specialistData.bio_de = formData.bio_de.trim();
        if (formData.specialization_de.trim()) specialistData.specialization_de = formData.specialization_de.trim();
        
        if (formData.name_fr.trim()) specialistData.name_fr = formData.name_fr.trim();
        if (formData.title_fr.trim()) specialistData.title_fr = formData.title_fr.trim();
        if (formData.bio_fr.trim()) specialistData.bio_fr = formData.bio_fr.trim();
        if (formData.specialization_fr.trim()) specialistData.specialization_fr = formData.specialization_fr.trim();

        if (isEditMode) {
          // Update existing specialist
          // Handle audio removal if audio was removed
          if (audioFile === null && !formData.audioUrl) {
            specialistData.audio_url = null;
          } else if (!audioUrl) {
            // Keep existing audio if no new file uploaded
            delete specialistData.audio_url;
          }
          
          const { error: updateError } = await supabase
            .from('specialists')
            .update(specialistData)
            .eq('id', id);

          if (updateError) {
            throw updateError;
          }

          setSuccessMessage('Specialist updated successfully!');
        } else {
          // Insert new specialist
          const { error: insertError } = await supabase
            .from('specialists')
            .insert([specialistData]);

          if (insertError) {
            throw insertError;
          }

          setSuccessMessage('Specialist added successfully!');
        }

        // Reset form after successful submission
        setTimeout(() => {
          if (!isEditMode) {
            setFormData({
              name: '',
              title: '',
              bio: '',
              image: '',
              email: '',
              phone: '',
              specialization: '',
              experience_years: '',
              languages: '',
              is_active: true,
              display_order: '',
              audioUrl: '',
              name_ar: '',
              title_ar: '',
              bio_ar: '',
              specialization_ar: '',
              name_ru: '',
              title_ru: '',
              bio_ru: '',
              specialization_ru: '',
              name_de: '',
              title_de: '',
              bio_de: '',
              specialization_de: '',
              name_fr: '',
              title_fr: '',
              bio_fr: '',
              specialization_fr: ''
            });
            setImageFile(null);
            setImagePreview(null);
            setAudioFile(null);
          }
          setSuccessMessage('');
        }, 2000);
      } catch (err) {
        console.error('Error saving specialist:', err);
        setErrorMessage(err.message || 'Failed to save specialist. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      image: ''
    }));
  };

  if (isLoadingData) {
    return (
      <div className="admin-form-container">
        <div className="loading-message">Loading specialist data...</div>
      </div>
    );
  }

  return (
    <div className="admin-form-container">
      <div className="form-header">
        <h1 className="form-title">{isEditMode ? 'Edit Specialist' : 'Add New Specialist'}</h1>
        <button 
          className="btn-back"
          onClick={() => navigate('/admin-dashboard/specialists')}
        >
          ← Back to Specialists
        </button>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {errorMessage && (
        <div className="error-message">{errorMessage}</div>
      )}

      <form onSubmit={handleSubmit} className="admin-form">
        {/* Basic Information */}
        <div className="form-section">
          <h2 className="section-title">Basic Information</h2>
          
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`form-input ${errors.name ? 'error' : ''}`}
              placeholder="Enter specialist name"
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="title" className="form-label">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., Energy Healer, Reiki Master"
            />
          </div>

          <div className="form-group">
            <label htmlFor="bio" className="form-label">
              Bio <span className="required">*</span>
            </label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              className={`form-textarea ${errors.bio ? 'error' : ''}`}
              placeholder="Enter specialist biography (minimum 20 characters)"
              rows="5"
            />
            {errors.bio && <span className="error-text">{errors.bio}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="specialization" className="form-label">Specialization</label>
            <input
              type="text"
              id="specialization"
              name="specialization"
              value={formData.specialization}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., Reiki, Energy Healing, Meditation"
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="form-section">
          <h2 className="section-title">Contact Information</h2>
          
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="specialist@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-input"
              placeholder="+1234567890"
            />
          </div>
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h2 className="section-title">Additional Information</h2>
          
          <div className="form-group">
            <label htmlFor="experience_years" className="form-label">Years of Experience</label>
            <input
              type="number"
              id="experience_years"
              name="experience_years"
              value={formData.experience_years}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., 10"
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="languages" className="form-label">Languages (comma-separated)</label>
            <input
              type="text"
              id="languages"
              name="languages"
              value={formData.languages}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., English, Arabic, Russian"
            />
          </div>

          <div className="form-group">
            <label htmlFor="display_order" className="form-label">Display Order</label>
            <input
              type="number"
              id="display_order"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              className="form-input"
              placeholder="Lower numbers appear first"
              min="0"
            />
          </div>

          <div className="form-group">
            <label className="form-checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="form-checkbox"
              />
              <span>Active (show on website)</span>
            </label>
          </div>
        </div>

        {/* Image Upload */}
        <div className="form-section">
          <h2 className="section-title">Image</h2>
          
          <div className="form-group">
            <label htmlFor="image" className="form-label">
              Specialist Photo {!isEditMode && <span className="required">*</span>}
            </label>
            <input
              type="file"
              id="image"
              name="image"
              accept="image/*"
              onChange={handleChange}
              className={`form-file-input ${errors.image ? 'error' : ''}`}
            />
            {errors.image && <span className="error-text">{errors.image}</span>}
            
            {imagePreview && (
              <div className="image-preview-container">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="btn-remove-image"
                >
                  Remove Image
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Audio Upload */}
        <div className="form-section">
          <h2 className="section-title">Audio File (Optional)</h2>
          
          <div className="form-group">
            <label htmlFor="audio" className="form-label">
              Audio File (MP3) (Optional)
              {isEditMode && formData.audioUrl && <span className="optional-text"> - Current audio will be replaced</span>}
            </label>
            <input
              type="file"
              id="audio"
              name="audio"
              accept="audio/mpeg,audio/mp3,.mp3"
              onChange={handleChange}
              className={`form-file-input ${errors.audio ? 'error' : ''}`}
            />
            {errors.audio && <span className="error-text">{errors.audio}</span>}
            
            {isEditMode && formData.audioUrl && !audioFile && (
              <div className="audio-preview" style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'flex-start' }}>
                <audio controls src={formData.audioUrl} style={{ width: '100%', maxWidth: '400px' }}>
                  Your browser does not support the audio element.
                </audio>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <a 
                    href={formData.audioUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: 'rgba(124, 58, 237, 0.1)',
                      border: '1px solid rgba(124, 58, 237, 0.3)',
                      borderRadius: '8px',
                      color: '#7C3AED',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                    download
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download Audio
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, audioUrl: '' }));
                      setAudioFile(null);
                      const audioInput = document.getElementById('audio');
                      if (audioInput) audioInput.value = '';
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '8px',
                      color: '#EF4444',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Remove Audio
                  </button>
                </div>
              </div>
            )}
            {audioFile && (
              <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'rgba(124, 58, 237, 0.05)', borderRadius: '8px', fontSize: '0.875rem', color: '#7C3AED' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{audioFile.name}</span>
                  <span>{(audioFile.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Translation Section */}
        <div className="form-section">
          <h2 className="section-title">Translations (Optional)</h2>
          <p className="section-note">
            Add translations to display specialists in Arabic, Russian, German, and French. If not provided, the specialist will display in English.
          </p>

          {/* Arabic Translations */}
          <div className="translation-group">
            <h3 className="translation-group-title">Arabic Translations</h3>
            <div className="form-group">
              <label htmlFor="name_ar" className="form-label">Name (Arabic)</label>
              <input
                type="text"
                id="name_ar"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleChange}
                className="form-input"
                placeholder="الاسم بالعربية"
              />
            </div>
            <div className="form-group">
              <label htmlFor="title_ar" className="form-label">Title (Arabic)</label>
              <input
                type="text"
                id="title_ar"
                name="title_ar"
                value={formData.title_ar}
                onChange={handleChange}
                className="form-input"
                placeholder="اللقب بالعربية"
              />
            </div>
            <div className="form-group">
              <label htmlFor="specialization_ar" className="form-label">Specialization (Arabic)</label>
              <input
                type="text"
                id="specialization_ar"
                name="specialization_ar"
                value={formData.specialization_ar}
                onChange={handleChange}
                className="form-input"
                placeholder="التخصص بالعربية"
              />
            </div>
            <div className="form-group">
              <label htmlFor="bio_ar" className="form-label">Bio (Arabic)</label>
              <textarea
                id="bio_ar"
                name="bio_ar"
                value={formData.bio_ar}
                onChange={handleChange}
                className="form-textarea"
                placeholder="السيرة الذاتية بالعربية"
                rows="4"
              />
            </div>
          </div>

          {/* Russian Translations */}
          <div className="translation-group">
            <h3 className="translation-group-title">Russian Translations</h3>
            <div className="form-group">
              <label htmlFor="name_ru" className="form-label">Name (Russian)</label>
              <input
                type="text"
                id="name_ru"
                name="name_ru"
                value={formData.name_ru}
                onChange={handleChange}
                className="form-input"
                placeholder="Имя на русском"
              />
            </div>
            <div className="form-group">
              <label htmlFor="title_ru" className="form-label">Title (Russian)</label>
              <input
                type="text"
                id="title_ru"
                name="title_ru"
                value={formData.title_ru}
                onChange={handleChange}
                className="form-input"
                placeholder="Должность на русском"
              />
            </div>
            <div className="form-group">
              <label htmlFor="specialization_ru" className="form-label">Specialization (Russian)</label>
              <input
                type="text"
                id="specialization_ru"
                name="specialization_ru"
                value={formData.specialization_ru}
                onChange={handleChange}
                className="form-input"
                placeholder="Специализация на русском"
              />
            </div>
            <div className="form-group">
              <label htmlFor="bio_ru" className="form-label">Bio (Russian)</label>
              <textarea
                id="bio_ru"
                name="bio_ru"
                value={formData.bio_ru}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Биография на русском"
                rows="4"
              />
            </div>
          </div>

          {/* German Translations */}
          <div className="translation-group">
            <h3 className="translation-group-title">German Translations</h3>
            <div className="form-group">
              <label htmlFor="name_de" className="form-label">Name (German)</label>
              <input
                type="text"
                id="name_de"
                name="name_de"
                value={formData.name_de}
                onChange={handleChange}
                className="form-input"
                placeholder="Name auf Deutsch"
              />
            </div>
            <div className="form-group">
              <label htmlFor="title_de" className="form-label">Title (German)</label>
              <input
                type="text"
                id="title_de"
                name="title_de"
                value={formData.title_de}
                onChange={handleChange}
                className="form-input"
                placeholder="Titel auf Deutsch"
              />
            </div>
            <div className="form-group">
              <label htmlFor="specialization_de" className="form-label">Specialization (German)</label>
              <input
                type="text"
                id="specialization_de"
                name="specialization_de"
                value={formData.specialization_de}
                onChange={handleChange}
                className="form-input"
                placeholder="Spezialisierung auf Deutsch"
              />
            </div>
            <div className="form-group">
              <label htmlFor="bio_de" className="form-label">Bio (German)</label>
              <textarea
                id="bio_de"
                name="bio_de"
                value={formData.bio_de}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Biografie auf Deutsch"
                rows="4"
              />
            </div>
          </div>

          {/* French Translations */}
          <div className="translation-group">
            <h3 className="translation-group-title">French Translations</h3>
            <div className="form-group">
              <label htmlFor="name_fr" className="form-label">Name (French)</label>
              <input
                type="text"
                id="name_fr"
                name="name_fr"
                value={formData.name_fr}
                onChange={handleChange}
                className="form-input"
                placeholder="Nom en français"
              />
            </div>
            <div className="form-group">
              <label htmlFor="title_fr" className="form-label">Title (French)</label>
              <input
                type="text"
                id="title_fr"
                name="title_fr"
                value={formData.title_fr}
                onChange={handleChange}
                className="form-input"
                placeholder="Titre en français"
              />
            </div>
            <div className="form-group">
              <label htmlFor="specialization_fr" className="form-label">Specialization (French)</label>
              <input
                type="text"
                id="specialization_fr"
                name="specialization_fr"
                value={formData.specialization_fr}
                onChange={handleChange}
                className="form-input"
                placeholder="Spécialisation en français"
              />
            </div>
            <div className="form-group">
              <label htmlFor="bio_fr" className="form-label">Bio (French)</label>
              <textarea
                id="bio_fr"
                name="bio_fr"
                value={formData.bio_fr}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Biographie en français"
                rows="4"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin-dashboard/specialists')}
            className="btn-cancel"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : (isEditMode ? 'Update Specialist' : 'Add Specialist')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminAddSpecialist;
